import type { DeviceEnumerator } from './DeviceEnumerator';
import type { DeviceDiff,NormalizedDevice } from './types';

/** Polling intervals in milliseconds */
const RECORDING_POLL_MS = 3000;
const IDLE_POLL_MS = 10000;

/** Debounce time for devicechange events */
const DEBOUNCE_MS = 500;

export type DeviceChangeMode = 'recording' | 'idle';

type DeviceChangeHandler = (diff: DeviceDiff) => void;
type DeviceLostHandler = (device: NormalizedDevice) => void;

/**
 * Detects device list changes via multiple signals:
 * - Primary: navigator.mediaDevices.ondevicechange (debounced)
 * - Fallback: adaptive polling (3s recording, 10s idle, OFF when hidden)
 * - Track observation: track.onended + readyState checks
 */
export class DeviceChangeCoordinator {
  private mode: DeviceChangeMode = 'idle';
  private running = false;
  private pollTimerId: ReturnType<typeof setInterval> | null = null;
  private debounceTimerId: ReturnType<typeof setTimeout> | null = null;
  private cachedDevices: NormalizedDevice[] = [];
  private observedTracks: MediaStreamTrack[] = [];

  private onDevicesChanged: DeviceChangeHandler | null = null;
  private onDeviceLost: DeviceLostHandler | null = null;

  // Bound handlers for cleanup
  private boundDeviceChangeHandler: () => void;
  private boundVisibilityHandler: () => void;

  constructor(private enumerator: DeviceEnumerator) {
    this.boundDeviceChangeHandler = this.handleDeviceChange.bind(this);
    this.boundVisibilityHandler = this.handleVisibilityChange.bind(this);
  }

  /** Set the callback for device list changes */
  setOnDevicesChanged(handler: DeviceChangeHandler | null): void {
    this.onDevicesChanged = handler;
  }

  /** Set the callback for when the active device is lost */
  setOnDeviceLost(handler: DeviceLostHandler | null): void {
    this.onDeviceLost = handler;
  }

  /** Start monitoring for device changes */
  start(mode: DeviceChangeMode): void {
    if (this.running) this.stop();

    this.mode = mode;
    this.running = true;
    this.cachedDevices = this.enumerator.getCachedDevices();

    // Primary signal: devicechange event
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', this.boundDeviceChangeHandler);
    }

    // Visibility-aware polling
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);

    // Start polling if tab is visible
    if (document.visibilityState === 'visible') {
      this.startPolling();
    }
  }

  /** Stop all monitoring and clean up */
  stop(): void {
    this.running = false;

    // Remove event listeners
    if (navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.boundDeviceChangeHandler);
    }
    document.removeEventListener('visibilitychange', this.boundVisibilityHandler);

    // Clear timers
    this.stopPolling();
    if (this.debounceTimerId !== null) {
      clearTimeout(this.debounceTimerId);
      this.debounceTimerId = null;
    }

    // Clean up track observers
    this.cleanupTrackObservers();
  }

  /** Change the monitoring mode (adjusts polling interval) */
  setMode(mode: DeviceChangeMode): void {
    if (this.mode === mode) return;
    this.mode = mode;

    // Restart polling with new interval
    if (this.running && document.visibilityState === 'visible') {
      this.stopPolling();
      this.startPolling();
    }
  }

  /** Observe a track for ended events and readyState changes */
  observeTrack(track: MediaStreamTrack): void {
    track.addEventListener('ended', () => {
      this.handleTrackEnded(track);
    });
    this.observedTracks.push(track);
  }

  /** Get the current polling interval */
  getPollingInterval(): number {
    return this.mode === 'recording' ? RECORDING_POLL_MS : IDLE_POLL_MS;
  }

  /** Whether the coordinator is currently running */
  isRunning(): boolean {
    return this.running;
  }

  private startPolling(): void {
    this.stopPolling();
    const interval = this.getPollingInterval();
    this.pollTimerId = setInterval(() => void this.poll(), interval);
  }

  private stopPolling(): void {
    if (this.pollTimerId !== null) {
      clearInterval(this.pollTimerId);
      this.pollTimerId = null;
    }
  }

  private handleDeviceChange(): void {
    // Debounce to coalesce rapid events
    if (this.debounceTimerId !== null) {
      clearTimeout(this.debounceTimerId);
    }
    this.debounceTimerId = setTimeout(() => {
      this.debounceTimerId = null;
      this.poll();
    }, DEBOUNCE_MS);
  }

  private handleVisibilityChange(): void {
    if (!this.running) return;

    if (document.visibilityState === 'visible') {
      // Tab became visible — poll immediately and restart interval
      this.poll();
      this.startPolling();
    } else {
      // Tab hidden — stop polling
      this.stopPolling();
    }
  }

  private handleTrackEnded(track: MediaStreamTrack): void {
    // Find the device associated with this track
    const settings = track.getSettings();
    const deviceId = settings.deviceId;
    if (deviceId) {
      const device = this.enumerator.findByBrowserDeviceId(deviceId);
      if (device && this.onDeviceLost) {
        this.onDeviceLost(device);
      }
    }

    // Remove from observed list
    this.observedTracks = this.observedTracks.filter(t => t !== track);
  }

  /** Poll for device changes and emit diff */
  async poll(): Promise<void> {
    if (!this.running) return;

    try {
      const currentDevices = await this.enumerator.enumerate();
      const diff = this.computeDiff(this.cachedDevices, currentDevices);

      if (diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0) {
        this.cachedDevices = currentDevices;
        if (this.onDevicesChanged) {
          this.onDevicesChanged(diff);
        }
      } else {
        this.cachedDevices = currentDevices;
      }
    } catch {
      // Enumeration failed (e.g., permission revoked) — silently continue
    }
  }

  /** Compute the diff between two device lists by internalId */
  computeDiff(prev: NormalizedDevice[], current: NormalizedDevice[]): DeviceDiff {
    const prevMap = new Map(prev.map(d => [d.internalId, d]));
    const currentMap = new Map(current.map(d => [d.internalId, d]));

    const added: NormalizedDevice[] = [];
    const removed: NormalizedDevice[] = [];
    const changed: NormalizedDevice[] = [];

    // Find added and changed
    for (const [id, device] of currentMap) {
      const prevDevice = prevMap.get(id);
      if (!prevDevice) {
        added.push(device);
      } else if (
        prevDevice.isDefault !== device.isDefault ||
        prevDevice.browserDeviceId !== device.browserDeviceId
      ) {
        changed.push(device);
      }
    }

    // Find removed
    for (const [id, device] of prevMap) {
      if (!currentMap.has(id)) {
        removed.push(device);
      }
    }

    return { added, removed, changed };
  }

  private cleanupTrackObservers(): void {
    this.observedTracks = [];
  }
}
